import React, { useRef, useState } from 'react';
import { PencilSimple, Trash, Plus, Image as ImageIcon, X, Star } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { resolveMediaUrl } from '@/config/api';
import { supabase } from '@/lib/supabaseClient';
import {
  uploadVehicleGalleryImage,
  deleteVehicleGalleryImage,
  setMainVehicleGalleryImage,
} from '@/lib/vehicleImageStorage';
import { vehicleImage, TYPE_IMAGES, normalizeVehicle } from '@/hooks/useVehicles';

const TYPE_TO_ENUM = {
  Sedan: 'sedan',
  SUV: 'suv',
  MUV: 'suv',
  Traveller: 'tempo_traveller',
  Luxury: 'suv',
};

const TYPE_BADGE = {
  Sedan: 'bg-[#E3F2FD] text-[#1565C0]',
  SUV: 'bg-[#E8F5E9] text-[#2E7D32]',
  MUV: 'bg-[#FFF3E0] text-[#E65100]',
  Traveller: 'bg-[#F3E5F5] text-[#6A1B9A]',
  Luxury: 'bg-[#FCE4EC] text-[#AD1457]',
};

const EMPTY = {
  type: 'Sedan',
  model: '',
  registration_number: '',
  capacity: '4',
  rate_per_km: '11',
  ac: true,
  image_url: '',
  status: 'active',
  notes: '',
};

const fallbackPreview = (type) => TYPE_IMAGES[type] || TYPE_IMAGES.Sedan;

const validateImageFile = (file) => {
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowed.includes(file.type)) {
    toast.error('Please choose a JPG, PNG, or WEBP image');
    return false;
  }
  if (file.size > 5 * 1024 * 1024) {
    toast.error('Image must be 5 MB or smaller');
    return false;
  }
  return true;
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-[#EBEBEB] rounded-[10px] px-5 py-3.5">
    <div className="text-2xl font-bold text-[#1B2D5E] font-body">{value}</div>
    <div className="text-xs text-[#888] font-body mt-0.5">{label}</div>
  </div>
);

const VehiclesTab = ({ vehicles, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [galleryImages, setGalleryImages] = useState([]);
  const [pendingMain, setPendingMain] = useState(null);
  const [pendingMainPreview, setPendingMainPreview] = useState(null);
  const [pendingOthers, setPendingOthers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const mainFileRef = useRef(null);
  const otherFileRef = useRef(null);

  const active = vehicles.filter((v) => v.status === 'active').length;
  const inactive = vehicles.filter((v) => v.status !== 'active').length;

  const mainImage = galleryImages.find((img) => img.category === 'main');
  const otherImages = galleryImages.filter((img) => img.category === 'other');
  const vehicleId = editing?.id;

  const clearPendingMain = () => {
    if (pendingMainPreview) URL.revokeObjectURL(pendingMainPreview);
    setPendingMain(null);
    setPendingMainPreview(null);
    if (mainFileRef.current) mainFileRef.current.value = '';
  };

  const clearPendingOthers = () => {
    pendingOthers.forEach((item) => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });
    setPendingOthers([]);
    if (otherFileRef.current) otherFileRef.current.value = '';
  };

  const resetModal = () => {
    clearPendingMain();
    clearPendingOthers();
    setEditing(null);
    setForm(EMPTY);
    setGalleryImages([]);
  };

  const openAdd = () => {
    resetModal();
    setModalOpen(true);
  };

  const openEdit = (v) => {
    clearPendingMain();
    clearPendingOthers();
    setEditing(v);
    setGalleryImages(v.images || []);
    setForm({
      type: v.type,
      model: v.model,
      registration_number: v.registration_number || '',
      capacity: String(v.capacity),
      rate_per_km: String(v.rate_per_km),
      ac: v.ac !== false,
      image_url: v.image_url || '',
      status: v.status || 'active',
      notes: v.notes || '',
    });
    setModalOpen(true);
  };

  const uploadGalleryImage = async (id, file, category) =>
    uploadVehicleGalleryImage(id, file, category);

  const refreshGallery = async (id) => {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*, vehicle_images(*)')
      .eq('id', id)
      .single();
    if (error) throw error;
    const vehicle = normalizeVehicle(data);
    setGalleryImages(vehicle.images || []);
    setEditing(vehicle);
    setForm((prev) => ({ ...prev, image_url: vehicle.image_url || '' }));
    onRefresh();
  };

  const handleMainFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !validateImageFile(file)) return;

    if (vehicleId) {
      setUploading(true);
      try {
        await uploadGalleryImage(vehicleId, file, 'main');
        toast.success('Cover photo updated');
        await refreshGallery(vehicleId);
      } catch (err) {
        toast.error(err.message || 'Failed to upload cover photo');
      } finally {
        setUploading(false);
        if (mainFileRef.current) mainFileRef.current.value = '';
      }
      return;
    }

    clearPendingMain();
    setPendingMain(file);
    setPendingMainPreview(URL.createObjectURL(file));
  };

  const handleOtherFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const valid = files.filter((file) => validateImageFile(file));
    if (!valid.length) return;

    if (vehicleId) {
      setUploading(true);
      try {
        for (const file of valid) {
          await uploadGalleryImage(vehicleId, file, 'other');
        }
        toast.success(valid.length > 1 ? 'Photos added' : 'Photo added');
        await refreshGallery(vehicleId);
      } catch (err) {
        toast.error(err.message || 'Failed to upload photos');
      } finally {
        setUploading(false);
        if (otherFileRef.current) otherFileRef.current.value = '';
      }
      return;
    }

    setPendingOthers((prev) => [
      ...prev,
      ...valid.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      })),
    ]);
    if (otherFileRef.current) otherFileRef.current.value = '';
  };

  const removePendingOther = (id) => {
    setPendingOthers((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const deleteGalleryImage = async (imageId) => {
    if (!vehicleId) return;
    setUploading(true);
    try {
      await deleteVehicleGalleryImage(vehicleId, imageId);
      toast.success('Photo removed');
      await refreshGallery(vehicleId);
    } catch (err) {
      toast.error(err.message || 'Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  const setAsMain = async (imageId) => {
    if (!vehicleId) return;
    setUploading(true);
    try {
      await setMainVehicleGalleryImage(vehicleId, imageId);
      toast.success('Set as cover photo');
      await refreshGallery(vehicleId);
    } catch (err) {
      toast.error(err.message || 'Failed to set cover photo');
    } finally {
      setUploading(false);
    }
  };

  const removeMainImage = async () => {
    if (pendingMain) {
      clearPendingMain();
      return;
    }
    if (mainImage) {
      await deleteGalleryImage(mainImage.id);
    }
  };

  const uploadPendingImages = async (id) => {
    if (pendingMain) {
      await uploadGalleryImage(id, pendingMain, 'main');
      clearPendingMain();
    }
    for (const item of pendingOthers) {
      await uploadGalleryImage(id, item.file, 'other');
    }
    clearPendingOthers();
  };

  const save = async () => {
    if (!form.model.trim()) {
      toast.error('Model name is required');
      return;
    }

    const now = new Date().toISOString();
    const row = {
      type: form.type,
      model: form.model.trim(),
      registration_number: form.registration_number.trim() || null,
      capacity: parseInt(form.capacity, 10),
      rate_per_km: parseFloat(form.rate_per_km),
      ac: form.ac,
      image_url: null,
      status: form.status,
      notes: form.notes.trim() || null,
      vehicle_type: TYPE_TO_ENUM[form.type] || 'sedan',
      is_available: form.status === 'active',
      updated_at: now,
    };

    setUploading(true);
    try {
      let vehicle;
      if (editing) {
        const { data, error } = await supabase
          .from('vehicles')
          .update(row)
          .eq('id', editing.id)
          .select('*, vehicle_images(*)')
          .single();
        if (error) throw error;
        vehicle = normalizeVehicle(data);
      } else {
        const { data, error } = await supabase
          .from('vehicles')
          .insert({ ...row, id: crypto.randomUUID(), created_at: now })
          .select('*, vehicle_images(*)')
          .single();
        if (error) throw error;
        vehicle = normalizeVehicle(data);
      }

      if (pendingMain || pendingOthers.length) {
        await uploadPendingImages(vehicle.id);
        const { data, error } = await supabase
          .from('vehicles')
          .select('*, vehicle_images(*)')
          .eq('id', vehicle.id)
          .single();
        if (error) throw error;
        vehicle = normalizeVehicle(data);
      }

      toast.success('✓ Vehicle saved successfully');
      setModalOpen(false);
      resetModal();
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to save vehicle');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.model}?`)) return;
    try {
      const { error } = await supabase.from('vehicles').delete().eq('id', v.id);
      if (error) throw error;
      toast.success('Vehicle deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete vehicle');
    }
  };

  const statusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    if (status === 'maintenance') return <Badge className="bg-orange-100 text-orange-800">In Service</Badge>;
    return <Badge className="bg-red-100 text-red-800">Inactive</Badge>;
  };

  const mainPreviewSrc = pendingMainPreview
    || (mainImage ? resolveMediaUrl(mainImage.url) || mainImage.url : null)
    || fallbackPreview(form.type);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-display text-[#1B2D5E]">Manage Vehicles</h2>
          <p className="text-sm text-[#888] font-body mt-1">Fleet available for booking</p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-[#1B2D5E] text-white rounded-lg px-5 py-2.5 text-sm font-medium font-body hover:bg-[#E8651A] transition-colors"
        >
          <Plus size={16} weight="bold" />
          Add Vehicle
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Vehicles" value={vehicles.length} />
        <StatCard label="Active" value={active} />
        <StatCard label="Inactive" value={inactive} />
      </div>

      <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-body text-xs">#</TableHead>
              <TableHead className="font-body text-xs">Image</TableHead>
              <TableHead className="font-body text-xs">Type</TableHead>
              <TableHead className="font-body text-xs">Model</TableHead>
              <TableHead className="font-body text-xs">Reg. Number</TableHead>
              <TableHead className="font-body text-xs">Capacity</TableHead>
              <TableHead className="font-body text-xs">Rate/km</TableHead>
              <TableHead className="font-body text-xs">AC</TableHead>
              <TableHead className="font-body text-xs">Status</TableHead>
              <TableHead className="font-body text-xs">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-[#888] font-body py-8">
                  No vehicles yet. Click &quot;Add Vehicle&quot; to get started.
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map((v, i) => (
                <TableRow key={v.id} className="font-body text-[13px] hover:bg-[#FAFAFA]" data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="py-3.5">{i + 1}</TableCell>
                  <TableCell>
                    <img
                      src={vehicleImage(v)}
                      alt={v.model}
                      className="w-14 h-10 object-cover rounded border border-[#EBEBEB]"
                    />
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[v.type] || 'bg-gray-100 text-gray-700'}`}>
                      {v.type}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{v.model}</TableCell>
                  <TableCell>{v.registration_number || '—'}</TableCell>
                  <TableCell>{v.capacity}+1</TableCell>
                  <TableCell className="font-semibold text-[#E8651A]">₹{v.rate_per_km}/km</TableCell>
                  <TableCell>
                    {v.ac !== false ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ AC</span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Non-AC</span>
                    )}
                  </TableCell>
                  <TableCell>{statusBadge(v.status)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)} title="Edit">
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(v)} title="Delete">
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) resetModal();
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto font-body">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-4">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Star size={14} weight="fill" className="text-[#E8651A]" />
                  Main Photo (Cover)
                </Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-4 items-start">
                  <div className="w-full sm:w-48 h-32 rounded-lg border-2 border-[#E8651A]/30 bg-[#FAFAFA] overflow-hidden flex items-center justify-center relative">
                    <img src={mainPreviewSrc} alt="Cover preview" className="w-full h-full object-cover" />
                    <span className="absolute top-1.5 left-1.5 bg-[#E8651A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Cover
                    </span>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={mainFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleMainFileSelect}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => mainFileRef.current?.click()}
                      disabled={uploading}
                    >
                      <ImageIcon size={16} className="mr-2" />
                      {mainImage || pendingMain ? 'Change Cover' : 'Upload Cover'}
                    </Button>
                    {(mainImage || pendingMain) && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={removeMainImage}
                        disabled={uploading}
                      >
                        <X size={16} className="mr-1" />
                        Remove Cover
                      </Button>
                    )}
                    <p className="text-[11px] text-[#888] font-body">
                      Shown as the primary fleet image on the homepage.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Other Photos</Label>
                <p className="text-[11px] text-[#888] font-body mt-0.5 mb-2">
                  Additional angles or interior shots. Visible in the fleet gallery.
                </p>
                <input
                  ref={otherFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleOtherFilesSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => otherFileRef.current?.click()}
                  disabled={uploading}
                >
                  <Plus size={14} className="mr-1" />
                  Add Photos
                </Button>

                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {otherImages.map((img) => (
                    <div key={img.id} className="relative group rounded-lg border border-[#EBEBEB] overflow-hidden aspect-[4/3]">
                      <img
                        src={resolveMediaUrl(img.url) || img.url}
                        alt="Vehicle"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                          type="button"
                          title="Set as cover"
                          onClick={() => setAsMain(img.id)}
                          disabled={uploading}
                          className="p-1.5 bg-white rounded-full text-[#1B2D5E] hover:bg-[#E8651A] hover:text-white"
                        >
                          <Star size={14} weight="fill" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => deleteGalleryImage(img.id)}
                          disabled={uploading}
                          className="p-1.5 bg-white rounded-full text-red-600 hover:bg-red-600 hover:text-white"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {pendingOthers.map((item) => (
                    <div key={item.id} className="relative rounded-lg border border-dashed border-[#CCC] overflow-hidden aspect-[4/3]">
                      <img src={item.preview} alt="Pending" className="w-full h-full object-cover opacity-80" />
                      <button
                        type="button"
                        onClick={() => removePendingOther(item.id)}
                        className="absolute top-1 right-1 p-1 bg-white rounded-full text-red-600 shadow"
                      >
                        <X size={12} />
                      </button>
                      <span className="absolute bottom-1 left-1 text-[9px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                        Pending
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Vehicle Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Sedan', 'SUV', 'MUV', 'Traveller', 'Luxury'].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Model Name *</Label>
              <Input className="mt-1" value={form.model} onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))} placeholder="Toyota Innova Crysta" />
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input className="mt-1" value={form.registration_number} onChange={(e) => setForm((p) => ({ ...p, registration_number: e.target.value }))} placeholder="GJ-03-AB-1234" />
            </div>
            <div>
              <Label>Seating Capacity *</Label>
              <Input type="number" min="1" className="mt-1" value={form.capacity} onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))} placeholder="4" />
            </div>
            <div>
              <Label>Rate per km (₹) *</Label>
              <Input type="number" min="1" step="0.5" className="mt-1" value={form.rate_per_km} onChange={(e) => setForm((p) => ({ ...p, rate_per_km: e.target.value }))} />
            </div>
            <div className="flex items-end gap-3 pb-1">
              <Switch checked={form.ac} onCheckedChange={(v) => setForm((p) => ({ ...p, ac: v }))} />
              <Label>AC</Label>
            </div>
            <div>
              <Label>Status *</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">In Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea className="mt-1" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Any special notes about vehicle" rows={3} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={uploading}>Cancel</Button>
            <Button className="bg-[#1B2D5E] hover:bg-[#E8651A]" onClick={save} disabled={uploading}>
              {uploading ? 'Saving…' : 'Save Vehicle'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesTab;
